import React from "react";
import { colorPairForGroup, groupColorFallback } from "./posterColorUtils";
import { applyColumnsOrder, fontFamilyCss, resolveWidgetOptions } from "./posterWidgetOptions";

const ALIGN_CLASS = { left: "text-left", center: "text-center", right: "text-right" };
function alignClass(value, fallback = "left") {
  return ALIGN_CLASS[value] || ALIGN_CLASS[fallback] || ALIGN_CLASS.left;
}

const baseTh =
  "border border-neutral-400 bg-neutral-200 px-1.5 py-0.5 font-bold text-neutral-800";
const baseTd = "border border-neutral-300 px-1.5 py-0.5 text-neutral-900";

// Blank white gutter inserted between campo blocks (cuadro horario / bracket
// "por campo") so adjacent campos read as separate and a match doesn't look
// like it spills into the next field. The cell carries no border, so the page
// background shows through between the bordered campo blocks.
const CAMPO_GAP_WIDTH = "0.9em";
const campoGapCell = "border-0 bg-white p-0";

function groupTeams(g) {
  return g.tournamentGroupTeams ?? g.TournamentGroupTeams ?? [];
}

function groupKey(g, i) {
  return g.id ?? g.Id ?? `g-${i}`;
}

function groupName(g) {
  return g.name ?? g.Name ?? "Grupo";
}

function groupColor(g) {
  return g?.color ?? g?.Color ?? "";
}

function teamObj(row) {
  return row.team ?? row.Team ?? null;
}

function teamShield(row) {
  const t = teamObj(row);
  return t?.shield ?? t?.Shield ?? "";
}

function teamName(row) {
  const t = teamObj(row);
  return t?.name ?? t?.Name ?? "—";
}

function formatDt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function matchRowFields(m) {
  const localN = m.localTeam?.name ?? m.LocalTeam?.Name ?? "";
  const awayN = m.foreignTeam?.name ?? m.ForeignTeam?.Name ?? "";
  const done = m.completed ?? m.Completed;
  const rawField = (m.fieldName ?? m.FieldName ?? "").trim();
  return {
    id: m.id ?? m.Id,
    localId: m.localTeamId ?? m.LocalTeamId ?? null,
    foreignId: m.foreignTeamId ?? m.ForeignTeamId ?? null,
    localN,
    awayN,
    done,
    dayHour: m.dayHour ?? m.DayHour,
    field: rawField || "—",
    localGoals: m.localGoals ?? m.LocalGoals ?? 0,
    foreignGoals: m.foreignGoals ?? m.ForeignGoals ?? 0,
    label: m.name ?? m.Name ?? "",
  };
}

function groupMatchesByField(rows) {
  const map = new Map();
  for (const m of rows) {
    const f = matchRowFields(m).field;
    if (!map.has(f)) map.set(f, []);
    map.get(f).push(m);
  }
  const entries = Array.from(map.entries()).map(([field, matches]) => {
    const sorted = [...matches].sort(
      (a, b) =>
        new Date(a.dayHour ?? a.DayHour ?? 0) - new Date(b.dayHour ?? b.DayHour ?? 0)
    );
    return { field, matches: sorted };
  });
  entries.sort((a, b) => {
    const da = new Date(a.matches[0]?.dayHour ?? a.matches[0]?.DayHour ?? 0);
    const db = new Date(b.matches[0]?.dayHour ?? b.matches[0]?.DayHour ?? 0);
    return da - db;
  });
  return entries;
}

function widgetRootStyle(opts) {
  return {
    fontFamily: fontFamilyCss(opts.fontFamily),
    fontSize: `${opts.fontSize}px`,
    lineHeight: 1.25,
  };
}

function buildTeamColorMap(groups) {
  const map = new Map();
  if (!Array.isArray(groups)) return map;
  groups.forEach((g, idx) => {
    const explicit = groupColor(g);
    const color = explicit || colorPairForGroup(idx).bg;
    for (const row of groupTeams(g)) {
      const teamId = row.teamId ?? row.TeamId ?? row.team?.id ?? row.Team?.Id;
      if (teamId) map.set(teamId, color);
    }
  });
  return map;
}

function buildTeamShieldMap(groups) {
  const map = new Map();
  if (!Array.isArray(groups)) return map;
  for (const g of groups) {
    for (const row of groupTeams(g)) {
      const t = teamObj(row);
      const teamId = row.teamId ?? row.TeamId ?? t?.id ?? t?.Id;
      const shield = t?.shield ?? t?.Shield ?? "";
      if (teamId && shield) map.set(teamId, shield);
    }
  }
  return map;
}

// ============================================================================
// Groups widget
// ============================================================================
function GroupStandingsTable({ g, opts }) {
  const cols = opts.columns || {};
  const showShield = cols.shield !== false && opts.showShield !== false;
  const showPts = cols.points !== false;
  const showGf = cols.goalsFor !== false;
  const showGc = cols.goalsAgainst !== false;
  const showPj = cols.played !== false;
  const teamAlign = alignClass(opts.align?.team, "left");
  const numAlign = alignClass(opts.align?.numbers, "center");

  return (
    <table className="w-full min-w-0 table-fixed border-collapse border border-neutral-400">
      <thead>
        <tr>
          <th className={`${baseTh} w-8 text-center`}>#</th>
          {showShield ? <th className={`${baseTh} w-7 text-center`}></th> : null}
          <th className={`${baseTh} ${teamAlign}`}>Equipo</th>
          {showPts ? <th className={`${baseTh} w-10 ${numAlign}`}>Pts</th> : null}
          {showGf ? <th className={`${baseTh} w-10 ${numAlign}`}>GF</th> : null}
          {showGc ? <th className={`${baseTh} w-10 ${numAlign}`}>GC</th> : null}
          {showPj ? <th className={`${baseTh} w-10 ${numAlign}`}>PJ</th> : null}
        </tr>
      </thead>
      <tbody>
        {groupTeams(g).map((row, i) => {
          const nm = teamName(row);
          const sh = teamShield(row);
          return (
            <tr key={row.id ?? row.Id ?? i}>
              <td className={`${baseTd} text-center`}>{i + 1}</td>
              {showShield ? (
                <td className={`${baseTd} text-center`} aria-hidden>
                  {sh ? <img src={sh} alt="" className="mx-auto inline-block h-4 w-4 object-contain" /> : null}
                </td>
              ) : null}
              <td className={`${baseTd} ${teamAlign} truncate`} title={nm}>
                {nm}
              </td>
              {showPts ? <td className={`${baseTd} ${numAlign}`}>{row.points ?? row.Points ?? 0}</td> : null}
              {showGf ? <td className={`${baseTd} ${numAlign}`}>{row.goals ?? row.Goals ?? 0}</td> : null}
              {showGc ? <td className={`${baseTd} ${numAlign}`}>{row.goalsReceived ?? row.GoalsReceived ?? 0}</td> : null}
              {showPj ? <td className={`${baseTd} ${numAlign}`}>{row.matchsPlayed ?? row.MatchsPlayed ?? 0}</td> : null}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function PosterWidgetGroups({ groups = [], flow = "vertical", options }) {
  const opts = resolveWidgetOptions("groups", options);
  if (!groups.length) {
    return (
      <div className="p-2 text-neutral-500" style={widgetRootStyle(opts)}>
        Sin grupos.
      </div>
    );
  }
  const orderedGroups = applyColumnsOrder(groups, opts.columnsOrder, (g, i) => String(g?.id ?? g?.Id ?? i));
  // Original index per group id, so palette fallback stays consistent when
  // the user reorders (Grupo A keeps its green even if you move it to col 3).
  const originalIndexById = new Map(groups.map((g, i) => [String(g?.id ?? g?.Id ?? i), i]));
  const horizontal = flow === "horizontal";
  const n = orderedGroups.length;
  const gridCols = horizontal
    ? { gridTemplateColumns: `repeat(${n}, minmax(96px, 1fr))` }
    : undefined;

  return (
    <div className="h-full w-full min-w-0 overflow-auto bg-white" style={widgetRootStyle(opts)}>
      <div
        className={horizontal ? "grid w-full min-w-0 gap-2 p-1" : "flex w-full min-w-0 flex-col gap-2 p-1"}
        style={gridCols}
      >
        {orderedGroups.map((g, gi) => {
          const origIdx = originalIndexById.get(String(g?.id ?? g?.Id ?? gi)) ?? gi;
          const explicit = groupColor(g);
          const auto = colorPairForGroup(origIdx);
          const headerBg = explicit || auto.bg;
          const headerFg = explicit ? "#0a0a0a" : auto.fg;
          return (
            <div key={groupKey(g, gi)} className="min-w-0">
              <div
                className="border border-b-0 border-neutral-400 px-2 py-1 font-bold"
                style={{ backgroundColor: headerBg, color: headerFg }}
              >
                {groupName(g)}
              </div>
              <GroupStandingsTable g={g} opts={opts} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Matches widget
// ============================================================================
function FieldSectionHeader({ title }) {
  return (
    <div className="border border-b-0 border-neutral-400 bg-neutral-300 px-2 py-1 font-bold text-neutral-900">
      {title}
    </div>
  );
}

function MatchCard({ m, showBracketLabel, hideFieldLine, accentColor, alignTeams = "left", alignNumbers = "center" }) {
  const f = matchRowFields(m);
  const score = f.done ? `${f.localGoals} - ${f.foreignGoals}` : "—";
  return (
    <div
      className="flex min-w-0 flex-col gap-0.5 border border-neutral-400 bg-white p-1.5 leading-tight text-neutral-900"
      style={accentColor ? { borderLeft: `4px solid ${accentColor}` } : undefined}
    >
      {showBracketLabel ? (
        <div className="truncate font-bold text-neutral-800" title={f.label || "Partido"}>
          {f.label || "—"}
        </div>
      ) : null}
      <div className="whitespace-nowrap font-bold text-neutral-800">{formatDt(f.dayHour)}</div>
      {!hideFieldLine ? (
        <div className="truncate text-neutral-600" title={f.field}>
          {f.field}
        </div>
      ) : null}
      <div className={`truncate ${alignClass(alignTeams, "left")}`} title={`${f.localN} vs ${f.awayN}`}>
        {f.localN || "—"} — {f.awayN || "—"}
      </div>
      <div className={`font-semibold ${alignClass(alignNumbers, "center")}`}>{score}</div>
    </div>
  );
}

function MatchesFieldTable({ matches, showLabelColumn, teamColorById, opts }) {
  const teamsAlign = alignClass(opts.align?.teams, "left");
  const numAlign = alignClass(opts.align?.numbers, "center");
  return (
    <table className="w-full min-w-0 table-fixed border-collapse border border-neutral-400">
      <thead>
        <tr>
          {showLabelColumn ? <th className={`${baseTh} w-[22%] ${teamsAlign}`}>Partido</th> : null}
          <th className={`${baseTh} ${showLabelColumn ? "w-[24%]" : "w-[26%]"} ${numAlign}`}>Fecha / hora</th>
          <th className={`${baseTh} ${teamsAlign}`}>Local</th>
          <th className={`${baseTh} ${teamsAlign}`}>Visitante</th>
          <th className={`${baseTh} w-20 ${numAlign}`}>Res.</th>
        </tr>
      </thead>
      <tbody>
        {matches.map((m) => {
          const f = matchRowFields(m);
          const localColor = teamColorById?.get(f.localId);
          const awayColor = teamColorById?.get(f.foreignId);
          return (
            <tr key={f.id}>
              {showLabelColumn ? (
                <td className={`${baseTd} ${teamsAlign} truncate`} title={f.label}>
                  {f.label || "—"}
                </td>
              ) : null}
              <td className={`${baseTd} ${numAlign} whitespace-nowrap`}>{formatDt(f.dayHour)}</td>
              <td
                className={`${baseTd} ${teamsAlign} truncate`}
                style={localColor ? { backgroundColor: localColor, color: "#0a0a0a" } : undefined}
                title={f.localN}
              >
                {f.localN || "—"}
              </td>
              <td
                className={`${baseTd} ${teamsAlign} truncate`}
                style={awayColor ? { backgroundColor: awayColor, color: "#0a0a0a" } : undefined}
                title={f.awayN}
              >
                {f.awayN || "—"}
              </td>
              <td className={`${baseTd} ${numAlign} whitespace-nowrap`}>
                {f.done ? `${f.localGoals} - ${f.foreignGoals}` : "—"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function PosterWidgetMatches({ matches = [], groups = [], flow = "vertical", options }) {
  const opts = resolveWidgetOptions("matches", options);
  if (!matches.length) {
    return (
      <div className="p-2 text-neutral-500" style={widgetRootStyle(opts)}>
        Sin partidos de grupo.
      </div>
    );
  }
  const byField = applyColumnsOrder(groupMatchesByField(matches), opts.columnsOrder, (e) => String(e.field));
  const teamColorById = buildTeamColorMap(groups);

  if (flow === "horizontal") {
    const nf = byField.length;
    return (
      <div className="h-full w-full min-w-0 overflow-auto bg-white p-1" style={widgetRootStyle(opts)}>
        <div className="grid min-w-0 gap-2" style={{ gridTemplateColumns: `repeat(${nf}, minmax(96px, 1fr))` }}>
          {byField.map(({ field, matches: rowsInField }) => (
            <div key={field} className="flex min-w-0 flex-col">
              {opts.showField !== false ? <FieldSectionHeader title={field} /> : null}
              <div className="flex min-h-0 min-w-0 flex-col gap-1.5 border border-t-0 border-neutral-400 bg-white p-1">
                {rowsInField.map((m) => {
                  const f = matchRowFields(m);
                  const accent = teamColorById.get(f.localId);
                  return (
                    <MatchCard
                      key={f.id}
                      m={m}
                      showBracketLabel={false}
                      hideFieldLine
                      accentColor={accent}
                      alignTeams={opts.align?.teams ?? "left"}
                      alignNumbers={opts.align?.numbers ?? "center"}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full min-w-0 overflow-auto bg-white p-1" style={widgetRootStyle(opts)}>
      <div className="flex min-w-0 flex-col gap-3">
        {byField.map(({ field, matches: rowsInField }) => (
          <div key={field} className="min-w-0">
            {opts.showField !== false ? <FieldSectionHeader title={field} /> : null}
            <MatchesFieldTable matches={rowsInField} showLabelColumn={false} teamColorById={teamColorById} opts={opts} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Schedule widget — matrix time × field, cells colored by group
// ============================================================================
export function PosterWidgetSchedule({ matches = [], groups = [], options }) {
  const opts = resolveWidgetOptions("schedule", options);
  if (!matches.length) {
    return (
      <div className="p-2 text-neutral-500" style={widgetRootStyle(opts)}>
        Sin partidos para el cuadro horario.
      </div>
    );
  }
  const teamColorById = buildTeamColorMap(groups);
  const teamGroupLetterById = new Map();
  if (Array.isArray(groups)) {
    groups.forEach((g, idx) => {
      const letter = String.fromCharCode(65 + idx);
      for (const row of groupTeams(g)) {
        const teamId = row.teamId ?? row.TeamId ?? row.team?.id ?? row.Team?.Id;
        if (teamId) teamGroupLetterById.set(teamId, letter);
      }
    });
  }

  const fields = [];
  const byTime = new Map();
  for (const m of matches) {
    const f = matchRowFields(m);
    const time = new Date(f.dayHour);
    if (Number.isNaN(time.getTime())) continue;
    const hhmm = time.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", hour12: false });
    const fld = f.field || "—";
    if (!fields.includes(fld)) fields.push(fld);
    if (!byTime.has(hhmm)) byTime.set(hhmm, { time: hhmm, cells: new Map() });
    byTime.get(hhmm).cells.set(fld, f);
  }
  // Apply user-defined column order on top of natural discovery order.
  const orderedFields = applyColumnsOrder(
    fields.map((f) => ({ id: f })),
    opts.columnsOrder,
    (x) => x.id,
  ).map((x) => x.id);
  const times = Array.from(byTime.values()).sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));
  const showLetter = opts.showGroupLetter !== false;
  const showResultBox = opts.showResultBox === true;
  const matchAlign = opts.align?.match ?? "left";
  const teamAlign = alignClass(matchAlign, "left");

  // Each campo is rendered as THREE columns — local | centro | visitante — so
  // every local name lines up in its own column and every visitante name in its
  // own, exactly like the printed cartel. A <colgroup> with table-fixed keeps the
  // team columns equal-width across all rows; the narrow centro column holds the
  // result box (to fill in by hand), the group letter, or a separator.
  return (
    <div className="h-full w-full min-w-0 overflow-auto bg-white p-1" style={widgetRootStyle(opts)}>
      <table className="w-full min-w-0 table-fixed border-collapse border border-neutral-400">
        <colgroup>
          <col style={{ width: "3.4em" }} />
          {orderedFields.map((fld, i) => (
            <React.Fragment key={fld}>
              <col />
              <col style={{ width: showResultBox ? "2.8em" : "1.6em" }} />
              <col />
              {i < orderedFields.length - 1 ? <col style={{ width: CAMPO_GAP_WIDTH }} /> : null}
            </React.Fragment>
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className={`${baseTh} text-center`}>Hora</th>
            {orderedFields.map((fld, i) => (
              <React.Fragment key={fld}>
                <th colSpan={3} className={`${baseTh} text-center`}>
                  {fld}
                </th>
                {i < orderedFields.length - 1 ? <th className={campoGapCell} aria-hidden /> : null}
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {times.map(({ time, cells }) => (
            <tr key={time}>
              <td className={`${baseTd} whitespace-nowrap font-bold text-center`}>{time}</td>
              {orderedFields.map((fld, i) => {
                const isLast = i === orderedFields.length - 1;
                const gap = isLast ? null : <td className={campoGapCell} aria-hidden />;
                const cell = cells.get(fld);
                if (!cell) {
                  return (
                    <React.Fragment key={fld}>
                      <td className={baseTd}>&nbsp;</td>
                      <td className={baseTd}>&nbsp;</td>
                      <td className={baseTd}>&nbsp;</td>
                      {gap}
                    </React.Fragment>
                  );
                }
                const localColor = teamColorById.get(cell.localId);
                const awayColor = teamColorById.get(cell.foreignId);
                const letter =
                  teamGroupLetterById.get(cell.localId) || teamGroupLetterById.get(cell.foreignId) || "";
                return (
                  <React.Fragment key={fld}>
                    <td
                      className={`${baseTd} truncate font-semibold ${teamAlign}`}
                      style={localColor ? { backgroundColor: localColor, color: "#0a0a0a" } : undefined}
                      title={cell.localN}
                    >
                      {cell.localN || "?"}
                    </td>
                    <td className={`${baseTd} text-center`}>
                      {showResultBox ? (
                        <span
                          className="mx-auto block h-[1.4em] w-full max-w-[2.6em] rounded-sm border border-neutral-500 bg-white"
                          aria-hidden
                        />
                      ) : showLetter && letter ? (
                        <span className="font-bold">{letter}</span>
                      ) : (
                        <span className="text-neutral-400">·</span>
                      )}
                    </td>
                    <td
                      className={`${baseTd} truncate font-semibold ${teamAlign}`}
                      style={awayColor ? { backgroundColor: awayColor, color: "#0a0a0a" } : undefined}
                      title={cell.awayN}
                    >
                      {cell.awayN || "?"}
                    </td>
                    {gap}
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Bracket widget — PDF-style with feeder labels + optional round columns
// ============================================================================

function ordinal(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x < 1) return `${n}º`;
  return `${x}º`;
}

// The /bracketmatchs DTO doesn't nest the group object, so gp.group?.name is
// usually empty and the label would read just "1º Grupo". Resolve the real
// group name ("Grupo B") from the groups data via groupId so the plaza is
// informative ("1º Grupo B"), matching the printed cartel.
function resolveGroupName(gp, groupNameById) {
  const nested = gp.group?.name ?? gp.Group?.name;
  if (nested) return nested;
  const gid = gp.groupId ?? gp.GroupId;
  const fromMap = gid != null ? groupNameById?.get(String(gid)) : null;
  return fromMap || "Grupo";
}

function feederLabelForGroupPosition(gp, groupNameById) {
  const gname = resolveGroupName(gp, groupNameById);
  const pos = Number(gp.position ?? gp.Position);
  if (Number.isFinite(pos) && pos >= 1) return `${ordinal(pos)} ${gname}`;
  return `${gname} — ${(gp.name ?? gp.Name ?? "Plaza").trim()}`;
}

function feederLabelForCrossGroup(cp, ci) {
  if (Number.isFinite(Number(cp)) && Number.isFinite(Number(ci))) {
    if (Number(ci) === 1) return `Mejor ${ordinal(cp)}`;
    return `${ordinal(ci)} mejor ${ordinal(cp)}`;
  }
  return null;
}

function feederLabelForLocal(m, groupNameById) {
  const gp = m.groupPositionLocal ?? m.GroupPositionLocal;
  if (gp) return feederLabelForGroupPosition(gp, groupNameById);
  return feederLabelForCrossGroup(
    m.crossGroupRankPositionLocal ?? m.CrossGroupRankPositionLocal,
    m.crossGroupRankIndexLocal ?? m.CrossGroupRankIndexLocal,
  );
}

function feederLabelForForeign(m, groupNameById) {
  const gp = m.groupPositionForeign ?? m.GroupPositionForeign;
  if (gp) return feederLabelForGroupPosition(gp, groupNameById);
  return feederLabelForCrossGroup(
    m.crossGroupRankPositionForeign ?? m.CrossGroupRankPositionForeign,
    m.crossGroupRankIndexForeign ?? m.CrossGroupRankIndexForeign,
  );
}

function bracketSlot({ side, m, showFeederLabels, showShield, teamColorById, teamShieldById, parentBySlot, groupNameById }) {
  const teamId = side === "local"
    ? (m.localTeamId ?? m.LocalTeamId ?? null)
    : (m.foreignTeamId ?? m.ForeignTeamId ?? null);
  const teamName = side === "local"
    ? (m.localTeam?.name ?? m.LocalTeam?.Name ?? "")
    : (m.foreignTeam?.name ?? m.ForeignTeam?.Name ?? "");
  const goals = side === "local"
    ? (m.localGoals ?? m.LocalGoals ?? null)
    : (m.foreignGoals ?? m.ForeignGoals ?? null);
  const isWinner = (m.winnerTeamId ?? m.WinnerTeamId) && teamId && (m.winnerTeamId ?? m.WinnerTeamId) === teamId;
  const color = teamId ? teamColorById?.get(teamId) : null;
  const shield = teamId && showShield ? teamShieldById?.get(teamId) : null;

  // Order of fallback labels when the team isn't assigned yet:
  //   1. assigned team name
  //   2. group-position feeder ("1º Grupo A" / "Mejor 3º")  — only if showFeederLabels
  //   3. parent-match winner ("Ganador Semi A")              — always (it's the most useful)
  //   4. "Por definir"
  let label = teamName;
  if (!label) {
    const feeder = side === "local" ? feederLabelForLocal(m, groupNameById) : feederLabelForForeign(m, groupNameById);
    if (feeder && showFeederLabels) {
      label = feeder;
    } else {
      const slots = parentBySlot?.get(m.id ?? m.Id);
      const parent = side === "local" ? slots?.local : slots?.foreign;
      if (parent) {
        const parentName = (parent.name ?? parent.Name ?? "").trim();
        label = parentName ? `Ganador ${parentName}` : "Ganador anterior";
      } else if (feeder) {
        label = feeder;
      } else {
        label = "Por definir";
      }
    }
  }

  return (
    <div
      className={`flex min-w-0 items-center justify-between gap-1.5 px-2 py-1 ${isWinner ? "font-extrabold" : "font-medium"}`}
      style={color ? { backgroundColor: color, color: "#0a0a0a" } : undefined}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        {shield ? <img src={shield} alt="" className="h-4 w-4 shrink-0 object-contain" /> : null}
        <span className="truncate" title={label}>{label}</span>
      </div>
      {goals != null ? <span className="shrink-0 tabular-nums">{goals}</span> : null}
    </div>
  );
}

function BracketFixture({ m, opts, teamColorById, teamShieldById, parentBySlot, groupNameById, compact = false }) {
  const f = matchRowFields(m);
  // In "compact" (by-field matrix) mode the time/field are carried by the row
  // and column, so we drop them here and only show the match name if it has one.
  const showHeader = !compact || !!f.label;
  return (
    <div className="flex min-w-0 flex-col overflow-hidden rounded border border-neutral-400 bg-white">
      {showHeader ? (
        <div className="flex items-center justify-between border-b border-neutral-300 bg-neutral-100 px-2 py-0.5 text-[0.85em] font-bold uppercase tracking-wide text-neutral-700">
          <span className="truncate" title={f.label || "Partido"}>{f.label || "Partido"}</span>
          {!compact ? <span className="ml-2 shrink-0 text-neutral-500">{formatDt(f.dayHour)}</span> : null}
        </div>
      ) : null}
      {bracketSlot({
        side: "local",
        m,
        showFeederLabels: opts.showFeederLabels !== false,
        showShield: opts.showShield === true,
        teamColorById,
        teamShieldById,
        parentBySlot,
        groupNameById,
      })}
      <div className="h-px bg-neutral-200" />
      {bracketSlot({
        side: "foreign",
        m,
        showFeederLabels: opts.showFeederLabels !== false,
        showShield: opts.showShield === true,
        teamColorById,
        teamShieldById,
        parentBySlot,
        groupNameById,
      })}
      {!compact && opts.showField !== false && f.field && f.field !== "—" ? (
        <div className="border-t border-neutral-200 bg-neutral-50 px-2 py-0.5 text-right text-[0.8em] text-neutral-500">
          {f.field}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Compute round depth for each match by walking NextMatchId forward.
 * Final = round 0 (no NextMatchId). Semifinal = round 1. Etc. We return matches
 * grouped by round, with the highest-round (earliest stage) first so the layout
 * reads left-to-right "Cuartos → Semifinal → Final" like the PDF.
 */
function computeBracketRounds(bracket) {
  const byId = new Map();
  for (const m of bracket) {
    byId.set(m.id ?? m.Id, m);
  }
  const depthCache = new Map();
  const depthOf = (id, seen = new Set()) => {
    if (depthCache.has(id)) return depthCache.get(id);
    if (seen.has(id)) return 0;
    seen.add(id);
    const m = byId.get(id);
    if (!m) return 0;
    const next = m.nextMatchId ?? m.NextMatchId ?? null;
    if (!next || !byId.has(next)) {
      depthCache.set(id, 0);
      return 0;
    }
    const d = depthOf(next, seen) + 1;
    depthCache.set(id, d);
    return d;
  };

  const grouped = new Map();
  for (const m of bracket) {
    const id = m.id ?? m.Id;
    const d = depthOf(id);
    if (!grouped.has(d)) grouped.set(d, []);
    grouped.get(d).push(m);
  }
  // Sort matches within each round by Order, then DayHour
  for (const arr of grouped.values()) {
    arr.sort((a, b) => {
      const oa = a.order ?? a.Order ?? 0;
      const ob = b.order ?? b.Order ?? 0;
      if (oa !== ob) return oa - ob;
      return new Date(a.dayHour ?? a.DayHour ?? 0) - new Date(b.dayHour ?? b.DayHour ?? 0);
    });
  }
  const maxDepth = grouped.size === 0 ? 0 : Math.max(...grouped.keys());
  // Build ordered list of [depth, matches[]] from deepest (earliest) to 0 (final).
  const rounds = [];
  for (let d = maxDepth; d >= 0; d--) {
    if (grouped.has(d)) rounds.push({ depth: d, matches: grouped.get(d) });
  }
  return rounds;
}

function roundLabelForDepth(depth, maxDepth) {
  const stagesFromFinal = maxDepth - depth; // 0 = earliest, maxDepth = final
  // The user-visible name; we count from final back.
  const fromFinal = depth;
  if (fromFinal === 0) return "Final";
  if (fromFinal === 1) return "Semifinal";
  if (fromFinal === 2) return "Cuartos";
  if (fromFinal === 3) return "Octavos";
  if (fromFinal === 4) return "16avos";
  return `Ronda ${stagesFromFinal + 1}`;
}

/**
 * For each child match, decide which parent (if any) feeds the local slot and which feeds the foreign slot.
 * Heuristic: if the child already has a GroupPosition/CrossGroupRank in that slot, the slot is "seeded"
 * by groups, so we don't assign a parent there. Remaining parents are sorted by (Order, DayHour)
 * ascending and fill local first, then foreign.
 */
function buildParentBySlot(bracket) {
  const parents = new Map(); // childId → Match[]
  for (const m of bracket) {
    const next = m.nextMatchId ?? m.NextMatchId;
    if (!next) continue;
    if (!parents.has(next)) parents.set(next, []);
    parents.get(next).push(m);
  }
  const result = new Map();
  for (const m of bracket) {
    const id = m.id ?? m.Id;
    const arr = (parents.get(id) ?? []).slice().sort((a, b) => {
      const oa = a.order ?? a.Order ?? 0;
      const ob = b.order ?? b.Order ?? 0;
      if (oa !== ob) return oa - ob;
      return new Date(a.dayHour ?? a.DayHour ?? 0) - new Date(b.dayHour ?? b.DayHour ?? 0);
    });
    const hasLocalSeed =
      (m.groupPositionLocalId ?? m.GroupPositionLocalId) ||
      (m.crossGroupRankPositionLocal ?? m.CrossGroupRankPositionLocal);
    const hasForeignSeed =
      (m.groupPositionForeignId ?? m.GroupPositionForeignId) ||
      (m.crossGroupRankPositionForeign ?? m.CrossGroupRankPositionForeign);
    const slots = { local: null, foreign: null };
    const remaining = [...arr];
    if (!hasLocalSeed && remaining.length > 0) slots.local = remaining.shift();
    if (!hasForeignSeed && remaining.length > 0) slots.foreign = remaining.shift();
    result.set(id, slots);
  }
  return result;
}

export function PosterWidgetBracket({ bracket = [], groups = [], flow = "vertical", options }) {
  const opts = resolveWidgetOptions("bracket", options);
  if (!bracket.length) {
    return (
      <div className="p-2 text-neutral-500" style={widgetRootStyle(opts)}>
        Sin partidos de eliminatoria.
      </div>
    );
  }
  const teamColorById = buildTeamColorMap(groups);
  const teamShieldById = buildTeamShieldMap(groups);
  const parentBySlot = buildParentBySlot(bracket);
  const groupNameById = new Map(
    (Array.isArray(groups) ? groups : []).map((g, i) => [String(g?.id ?? g?.Id ?? i), groupName(g)]),
  );
  const rounds = computeBracketRounds(bracket);
  const maxDepth = Math.max(...rounds.map((r) => r.depth), 0);

  // "fields" layout — chronological cartel like the printed PDF: each round is a
  // section, and inside it a matrix of time (rows) × Campo (columns). Rounds run
  // earliest stage → final, so Octavos shows first, then Cuartos, Semifinal,
  // Final and 3º/4º puesto, each row in ascending time order.
  if (opts.bracketLayout === "fields") {
    const fieldSet = [];
    for (const m of bracket) {
      const fld = matchRowFields(m).field;
      if (!fieldSet.includes(fld)) fieldSet.push(fld);
    }
    const orderedFields = applyColumnsOrder(
      fieldSet.map((f) => ({ id: f })),
      opts.columnsOrder,
      (x) => x.id,
    ).map((x) => x.id);

    return (
      <div className="h-full w-full min-w-0 overflow-auto bg-white p-1" style={widgetRootStyle(opts)}>
        <div className="flex min-w-0 flex-col gap-3">
          {rounds.map(({ depth, matches }) => {
            const timeMap = new Map(); // hhmm → Map<field, Match[]>
            for (const m of matches) {
              const f = matchRowFields(m);
              const t = new Date(f.dayHour);
              const hhmm = Number.isNaN(t.getTime())
                ? "—"
                : t.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", hour12: false });
              if (!timeMap.has(hhmm)) timeMap.set(hhmm, new Map());
              const cells = timeMap.get(hhmm);
              if (!cells.has(f.field)) cells.set(f.field, []);
              cells.get(f.field).push(m);
            }
            const times = Array.from(timeMap.keys()).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
            return (
              <div key={depth} className="min-w-0">
                <FieldSectionHeader title={roundLabelForDepth(depth, maxDepth)} />
                <table className="w-full min-w-0 table-fixed border-collapse border border-t-0 border-neutral-400">
                  <colgroup>
                    <col style={{ width: "3.4em" }} />
                    {orderedFields.map((fld, i) => (
                      <React.Fragment key={fld}>
                        <col />
                        {i < orderedFields.length - 1 ? <col style={{ width: CAMPO_GAP_WIDTH }} /> : null}
                      </React.Fragment>
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      <th className={`${baseTh} text-center`}>Hora</th>
                      {orderedFields.map((fld, i) => (
                        <React.Fragment key={fld}>
                          <th className={`${baseTh} text-center`}>{fld}</th>
                          {i < orderedFields.length - 1 ? <th className={campoGapCell} aria-hidden /> : null}
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {times.map((time) => {
                      const cells = timeMap.get(time);
                      return (
                        <tr key={time}>
                          <td className={`${baseTd} whitespace-nowrap text-center font-bold`}>{time}</td>
                          {orderedFields.map((fld, i) => {
                            const ms = cells.get(fld) || [];
                            return (
                              <React.Fragment key={fld}>
                                <td className={`${baseTd} align-top`}>
                                  {ms.length ? (
                                    <div className="flex flex-col gap-1">
                                      {ms.map((mm) => (
                                        <BracketFixture
                                          key={mm.id ?? mm.Id}
                                          m={mm}
                                          opts={opts}
                                          teamColorById={teamColorById}
                                          teamShieldById={teamShieldById}
                                          parentBySlot={parentBySlot}
                                          groupNameById={groupNameById}
                                          compact
                                        />
                                      ))}
                                    </div>
                                  ) : (
                                    <span>&nbsp;</span>
                                  )}
                                </td>
                                {i < orderedFields.length - 1 ? (
                                  <td className={campoGapCell} aria-hidden />
                                ) : null}
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // The other two layouts group by ROUND (Octavos / Cuartos / Semifinal / Final).
  //   - "rounds"  → columns, one round per column (PDF-style, wide cartels).
  //   - "list"    → vertical stack, one round on top of the next (narrow cartels).
  if (opts.bracketLayout === "list") {
    return (
      <div className="h-full w-full min-w-0 overflow-auto bg-white p-1" style={widgetRootStyle(opts)}>
        <div className="flex min-w-0 flex-col gap-3">
          {rounds.map(({ depth, matches }) => (
            <div key={depth} className="min-w-0">
              <FieldSectionHeader title={roundLabelForDepth(depth, maxDepth)} />
              <div className="flex min-w-0 flex-col gap-1.5 border border-t-0 border-neutral-400 bg-white p-1.5">
                {matches.map((m) => (
                  <BracketFixture
                    key={m.id ?? m.Id}
                    m={m}
                    opts={opts}
                    teamColorById={teamColorById}
                    teamShieldById={teamShieldById}
                    parentBySlot={parentBySlot}
                    groupNameById={groupNameById}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Default: "rounds" layout — one column per round, deepest stage on the left.
  return (
    <div className="h-full w-full min-w-0 overflow-auto bg-white p-1" style={widgetRootStyle(opts)}>
      <div
        className="grid min-w-0 gap-2"
        style={{ gridTemplateColumns: `repeat(${Math.max(rounds.length, 1)}, minmax(160px, 1fr))` }}
      >
        {rounds.map(({ depth, matches }) => (
          <div key={depth} className="flex min-w-0 flex-col gap-2">
            <FieldSectionHeader title={roundLabelForDepth(depth, maxDepth)} />
            <div className="flex min-w-0 flex-col gap-2 border border-t-0 border-neutral-400 bg-white p-1.5">
              {matches.map((m) => (
                <BracketFixture
                  key={m.id ?? m.Id}
                  m={m}
                  opts={opts}
                  teamColorById={teamColorById}
                  teamShieldById={teamShieldById}
                  parentBySlot={parentBySlot}
                  groupNameById={groupNameById}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export { groupColorFallback };
